import type { HudView } from '../../game/gameView';
import {
  COMBO_AMP_THRESHOLD,
  COMBO_FLAME_THRESHOLD,
  SCORE_COMBO_CAP,
  TIME_ORANGE_THRESHOLD_S,
  TIME_PINK_THRESHOLD_S,
} from '../../shared/config';
import { createClockIcon, createPauseIcon, createStarIcon } from './icons';

export interface Hud extends HudView {
  readonly element: HTMLElement;
  destroy(): void;
}

export interface HudHandlers {
  onPause(): void;
}

function timeColor(seconds: number): string {
  if (seconds <= TIME_PINK_THRESHOLD_S) return 'var(--pink)';
  if (seconds <= TIME_ORANGE_THRESHOLD_S) return 'var(--orange)';
  return 'var(--teal)';
}

export function createHud(handlers: HudHandlers): Hud {
  const root = document.createElement('div');
  root.className = 'hud';

  const scoreBox = document.createElement('div');
  scoreBox.className = 'hud-card hud-score';
  scoreBox.appendChild(createStarIcon(2.625));
  const scoreInner = document.createElement('div');
  scoreInner.className = 'hud-card-text';
  const scoreLabel = document.createElement('div');
  scoreLabel.className = 'hud-card-label';
  scoreLabel.textContent = 'Score';
  const scoreValue = document.createElement('div');
  scoreValue.className = 'hud-card-value headline';
  scoreValue.dataset.testid = 'hud-score-value';
  scoreValue.textContent = '0';
  scoreInner.appendChild(scoreLabel);
  scoreInner.appendChild(scoreValue);
  scoreBox.appendChild(scoreInner);
  root.appendChild(scoreBox);

  const timerBox = document.createElement('div');
  timerBox.className = 'hud-card hud-timer';
  const clockHolder = document.createElement('div');
  clockHolder.className = 'hud-clock-holder';
  let clockEl = createClockIcon(3, false);
  clockHolder.appendChild(clockEl);
  timerBox.appendChild(clockHolder);
  const timerInner = document.createElement('div');
  timerInner.className = 'hud-timer-inner';
  const timerHeader = document.createElement('div');
  timerHeader.className = 'hud-timer-header';
  const timerLabel = document.createElement('span');
  timerLabel.className = 'hud-card-label';
  timerLabel.textContent = 'Time';
  const timerValue = document.createElement('span');
  timerValue.className = 'hud-timer-value headline';
  timerValue.dataset.testid = 'hud-timer-value';
  timerValue.textContent = '0s';
  timerHeader.appendChild(timerLabel);
  timerHeader.appendChild(timerValue);
  const timerBar = document.createElement('div');
  timerBar.className = 'hud-timer-bar';
  const timerFill = document.createElement('div');
  timerFill.className = 'hud-timer-fill';
  timerFill.style.setProperty('--fill', '100%');
  timerBar.appendChild(timerFill);
  timerInner.appendChild(timerHeader);
  timerInner.appendChild(timerBar);
  timerBox.appendChild(timerInner);
  root.appendChild(timerBox);

  const comboBox = document.createElement('div');
  comboBox.className = 'hud-card hud-combo';
  comboBox.dataset.level = 'low';
  const comboGlyph = document.createElement('div');
  comboGlyph.className = 'hud-combo-glyph';
  comboGlyph.textContent = '⭐';
  const comboInner = document.createElement('div');
  comboInner.className = 'hud-card-text';
  const comboLabel = document.createElement('div');
  comboLabel.className = 'hud-card-label';
  comboLabel.textContent = 'Combo';
  const comboValue = document.createElement('div');
  comboValue.className = 'hud-card-value headline';
  comboValue.dataset.testid = 'hud-combo-value';
  comboValue.textContent = '×0';
  comboInner.appendChild(comboLabel);
  comboInner.appendChild(comboValue);
  comboBox.appendChild(comboGlyph);
  comboBox.appendChild(comboInner);
  root.appendChild(comboBox);

  const pauseBtn = document.createElement('button');
  pauseBtn.className = 'hud-pause-btn';
  pauseBtn.type = 'button';
  pauseBtn.dataset.testid = 'pause-btn';
  pauseBtn.setAttribute('aria-label', 'Pause');
  pauseBtn.appendChild(createPauseIcon(1.75));
  pauseBtn.addEventListener('click', () => handlers.onPause());
  root.appendChild(pauseBtn);

  let lastTimePink = false;

  function setScore(n: number): void {
    scoreValue.textContent = String(n);
  }

  function setCombo(n: number): void {
    comboValue.textContent = `×${n}`;
    if (n >= COMBO_FLAME_THRESHOLD) {
      comboBox.dataset.level = 'flame';
      comboGlyph.textContent = '🔥';
    } else if (n >= COMBO_AMP_THRESHOLD) {
      comboBox.dataset.level = 'amp';
      comboGlyph.textContent = '⭐';
    } else {
      comboBox.dataset.level = 'low';
      comboGlyph.textContent = '⭐';
    }
    const scale = n >= COMBO_AMP_THRESHOLD
      ? 1 + Math.min(n, SCORE_COMBO_CAP) * 0.03
      : 1;
    comboBox.style.setProperty('--combo-scale', scale.toFixed(3));
  }

  function setTime(seconds: number, fractionRemaining: number): void {
    timerValue.textContent = `${seconds}s`;
    const color = timeColor(seconds);
    timerValue.style.color = color;
    timerFill.style.setProperty('--fill', `${(fractionRemaining * 100).toFixed(2)}%`);
    timerFill.style.setProperty('--fill-color', color);
    const pink = seconds <= TIME_PINK_THRESHOLD_S;
    if (pink !== lastTimePink) {
      lastTimePink = pink;
      const next = createClockIcon(3, pink);
      clockHolder.replaceChild(next, clockEl);
      clockEl = next;
    }
  }

  function getInsetTop(): number {
    return root.getBoundingClientRect().bottom;
  }

  function getInsetBottom(): number {
    return root.getBoundingClientRect().bottom;
  }

  function destroy(): void {
    root.remove();
  }

  return { element: root, setScore, setCombo, setTime, getInsetTop, getInsetBottom, destroy };
}
